import { usePricingRights } from '@util/PricingRightsHook';
import React from 'react';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { Navigate, Route, Routes } from 'react-router-dom';
import MarketStatePage from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/MarketStatePage';
import RateshopperDashboardPage from '@app/site/secured/rateshopper/rates/dashboard/page/RateshopperDashboardPage';
import MarketStateSnapshotCalendarPage
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/MarketStateSnapshotCalendarPage';
import MarketStateSnapshotTable
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/MarketStateSnapshotTable';
import MarketStateSnapshotGraphs
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_graph/MarketStateSnapshotGraphs';
import ParityPage from '@app/site/secured/rateshopper/rates/dashboard/fragment/__parity/page/ParityPage';
import ChannelCheckPage from '@app/site/secured/rateshopper/rates/channel-check/page/ChannelCheckPage';
import MetaradarPage from '@app/site/secured/rateshopper/rates/dashboard/fragment/__metaradar/page/MetaradarPage';
import MarketInsightPage
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/MarketInsightPage';
import FlexiSetsPage from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/page/FlexiSetsPage';
import ErrorPageNotFound from '@app/site/system/page/ErrorPageNotFound';
import AlertsPage from '@app/site/secured/rateshopper/rates/alert/page/AlertsPage';
import CreateFlexiSetPage
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/page/CreateFlexiSetPage';
import EditFlexiSetPage
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/page/EditFlexiSetPage';
import AlertPage from '@app/site/secured/rateshopper/rates/alert/page/AlertPage';
import AlertExecutionPage from '@app/site/secured/rateshopper/rates/alert-execution/page/AlertExecutionPage';
import ParityDashboardPage from '@app/site/secured/rateshopper/parity/ParityDashboardPage';
import RateshopperRoomMappingSettingsPage
	from '@app/site/secured/rateshopper/rates/settings/page/room-mapping/RateshopperRoomMappingSettingsPage';
import { RatesPromotion } from '@app/site/secured/rateshopper/rates/dashboard/fragment/promotion/RatesPromotion';
import RateshopperSettingsPage
	from '@app/site/secured/rateshopper/rates/settings/RateshopperSettingsPage';
import RmsDashboardPage from '@app/site/secured/rateshopper/rms/RmsDashboardPage';
import RateshopperCompetitorTagSettingsPage from '@app/site/secured/rateshopper/rates/settings/page/competitor-tags/RateshopperCompetitorTagSettingsPage';
import PricingAutomationDashboardPage
	from '@app/site/secured/rateshopper/pricing-automation/PricingAutomationDashboardPage';
import PricingRoomAnchoringPage
	from '@app/site/secured/rateshopper/pricing-automation/architecture/PricingRoomAnchoringPage';
import RateshopperGeneralSettingsPage from '@app/site/secured/rateshopper/rates/settings/page/RateshopperGeneralSettingsPage';
import RateshopperCalendarSettingsPage
	from '@app/site/secured/rateshopper/rates/settings/page/RateshopperCalendarSettingsPage';
import RateshopperPriceSettingsPage
	from '@app/site/secured/rateshopper/rates/settings/page/RateshopperPriceSettingsPage';
import RateshopperAvailabilitySettingsPage
	from '@app/site/secured/rateshopper/rates/settings/page/RateshopperAvailabilitySettingsPage';
import RateshopperCompetitorsSettingsPage
	from '@app/site/secured/rateshopper/rates/settings/page/competitors/RateshopperCompetitorsSettingsPage';
import AddNewCompanyHotelPage from '@app/site/secured/rateshopper/rates/add-new-property/AddNewCompanyHotelPage';
import MarketStateExportPage from '@app/site/secured/rateshopper/rates/market-state-export/MarketStateExportPage';
import PricingAutomationStrategyCompetitorsImportancePage
	from '@app/site/secured/rateshopper/pricing-automation/competitor-importance/PricingAutomationStrategyCompetitorsImportancePage';
import PricingSubmissionsLogPage
	from './pricing-automation/price-submission-log/PricingSubmissionsLogPage';
import PromotionsPage from '@app/site/secured/rateshopper/rates/promotions/PromotionsPage';
import BookingRankingPage from '@app/site/secured/rateshopper/rates/booking-ranking/BookingRankingPage';

const RateshopperModulesRouter = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();
	const { canChangePrices } = usePricingRights(companyHotel);
	const [isLoading, setIsLoading] = React.useState(false);


	return <Routes>
		{companyHotel.hasRatesModule() && <>
			<Route path="dashboard" element={<Navigate to="/secured/rateshopper/rates/market/calendar" replace />} />
			<Route path="rates" element={<RateshopperDashboardPage />} errorElement={<ErrorPageNotFound />}>
				<Route path='market-state-exports' element={<MarketStateExportPage />} />
				<Route path="market" element={<MarketStatePage setIsLoading={setIsLoading} />}>
					<Route path="calendar" element={<MarketStateSnapshotCalendarPage />} />
					<Route path="table" element={<MarketStateSnapshotTable isLoading={isLoading} />} />
					<Route path="graph" element={<MarketStateSnapshotGraphs />} />
				</Route>
				<Route path="parity" element={<ParityPage />} />
				<Route path="channels" element={<ChannelCheckPage />} />
				<Route path="metaradar" element={<MetaradarPage />} />
				<Route path="market-pulse" element={<MarketInsightPage />} />
				<Route path="promotions" element={<PromotionsPage />} />
				<Route path="booking-ranking" element={<BookingRankingPage view="simple" />} />
				<Route path="booking-ranking/detail" element={<BookingRankingPage view="detail" />} />
				<Route path="alerts" element={<AlertsPage />} />
				<Route path="alert">
					<Route path="create/:alertType" element={<AlertPage />} />
					<Route path="duplicate/:alertType/:alertId" element={<AlertPage isCopy />} />
					<Route path=":alertType/:alertId" element={<AlertPage />} />
					<Route path="execution/:alertExecutionId" element={<AlertExecutionPage />} />
				</Route>
				<Route path="settings" element={<RateshopperSettingsPage />}>
					<Route index element={<RateshopperGeneralSettingsPage />} />
					<Route path="calendar" element={<RateshopperCalendarSettingsPage />} />
					<Route path="price" element={<RateshopperPriceSettingsPage />} />
					<Route path="availability" element={<RateshopperAvailabilitySettingsPage />} />
					<Route path="room-mapping" element={<RateshopperRoomMappingSettingsPage />} />
					<Route path="competitors" element={<RateshopperCompetitorsSettingsPage />} />
					<Route path="competitor-groups" element={<RateshopperCompetitorTagSettingsPage />} />
				</Route>
				<Route path="add-new-property" element={<AddNewCompanyHotelPage />} />
				{companyHotel.hasFlexiSetsModule() && <>
					<Route path="flexi-sets">
						<Route index element={<FlexiSetsPage />} />
						<Route path="create" element={<CreateFlexiSetPage />} />
						<Route path=":competitorsGroupId" element={<EditFlexiSetPage />} />
					</Route>
				</>}
				{canChangePrices && <>
					<Route path="pricing-automation" element={<PricingAutomationDashboardPage />} errorElement={<ErrorPageNotFound />}>
						<Route index element={<PricingRoomAnchoringPage />} />
						{companyHotel.hasPriceRecommendationModule() && <Route path="competitors-importance" element={<PricingAutomationStrategyCompetitorsImportancePage />} />}
						<Route path="price-submission-log" element={<PricingSubmissionsLogPage />} />
					</Route>
				</>}
			</Route>

		</>}
		{companyHotel.hasParityModule() && <>
			{!companyHotel.hasRatesModule() &&
				<Route path="dashboard" element={<Navigate to="/secured/rateshopper/parity" replace />} />}
			<Route path="parity" element={<ParityDashboardPage />} errorElement={<ErrorPageNotFound />}>
				<Route index element={<ParityPage />} />
				<Route path="metaradar" element={<MetaradarPage />} />
				<Route path="market-pulse" element={<MarketInsightPage />} />
				<Route path="rates-promotion" element={<RatesPromotion />} />
			</Route>
		</>}
		{companyHotel.hasRatesRmsModule() && <>
			<Route path="dashboard" element={<Navigate to="/secured/rateshopper/rms" replace />} />
			<Route path="rms" element={<RmsDashboardPage />} errorElement={<ErrorPageNotFound />}>
				<Route index element={<RatesPromotion />} />
			</Route>
		</>}

		<Route path="*" element={<ErrorPageNotFound />} errorElement={<ErrorPageNotFound />} />
	</Routes>;
});

export default RateshopperModulesRouter;
